export type HotStackServerPhase = 'down' | 'up';
export type HotloadState = 'parityIdle' | 'hotStandby' | 'hotRouted';

export interface RuntimeModeResponse {
  mode?: string;
  routeMode?: string;
  hotloadState?: HotloadState;
  environment?: 'testing' | 'staging' | 'production';
  hotStack?: {
    phase?: HotStackServerPhase;
    healthy?: boolean;
    running?: boolean;
  };
  hotloadingControlEligible?: boolean;
  routeToggleEnabled?: boolean;
  routeToHotAllowed?: boolean;
  message?: string;
}

export class RuntimeApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RuntimeApiError';
    this.status = status;
  }
}

async function parseJsonResponse(res: Response): Promise<RuntimeModeResponse> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const body = await res.text();
    throw new Error(
      res.ok
        ? `Unexpected non-JSON response (HTTP ${res.status}). ${body.slice(0, 200)}`
        : `Request failed (HTTP ${res.status}). ${body.slice(0, 200) || 'Often a 502 when the portal upstream is down.'}`
    );
  }
  return (await res.json()) as RuntimeModeResponse;
}

export async function fetchRuntimeMode(token: string): Promise<RuntimeModeResponse> {
  const res = await fetch('/api/runtime/mode', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    const message = (data as { message?: string }).message || 'Failed to read runtime mode.';
    throw new RuntimeApiError(message, res.status);
  }
  return data;
}

/** Authoritative hotload snapshot (same contract as E2E POST /api/runtime/state). */
export async function fetchHotloadState(token: string): Promise<RuntimeModeResponse> {
  const res = await fetch('/api/runtime/state', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    const message = (data as { message?: string }).message || 'Failed to read hotload state.';
    throw new RuntimeApiError(message, res.status);
  }
  return data;
}

export async function postHotStackAction(token: string, action: 'up' | 'down'): Promise<RuntimeModeResponse> {
  const res = await fetch('/api/runtime/hot-stack', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ action })
  });
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    const message = (data as { message?: string }).message || `Failed to ${action} hot stack.`;
    throw new RuntimeApiError(message, res.status);
  }
  return data;
}

export async function postRouteMode(token: string, mode: 'hot' | 'parity'): Promise<RuntimeModeResponse> {
  const res = await fetch('/api/runtime/mode', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ mode })
  });
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    const message = (data as { message?: string }).message || 'Failed to switch route mode.';
    throw new RuntimeApiError(message, res.status);
  }
  return data;
}

export async function postHotloadState(
  token: string,
  state: HotloadState
): Promise<RuntimeModeResponse> {
  const res = await fetch('/api/runtime/state', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ state })
  });
  const data = await parseJsonResponse(res);
  if (!res.ok) {
    const message = (data as { message?: string }).message || 'Failed to set hotload state.';
    throw new RuntimeApiError(message, res.status);
  }
  return data;
}

/** Stack badge: containers running (independent of Caddy route or HTTP health blips). */
export function stackPhaseFromServer(data: RuntimeModeResponse): 'down' | 'up' {
  if (data.hotStack?.running || data.hotStack?.phase === 'up') {
    return 'up';
  }
  return 'down';
}
